import type { NodeTrace } from "../types";

/**
 * The compiled graph's shape, mirroring `backend/app/graph/builder.py`.
 *
 * Kept as data rather than drawn by hand so the diagram cannot quietly disagree with the
 * graph: the layers below are the render order, and `successors` is what lets the view
 * light up the nodes that *could* run next while one is still in flight.
 *
 * If a node is added to the backend, `GRAPH_NODES` is where the UI learns about it - an
 * unknown node arriving on the wire is surfaced rather than silently dropped.
 */

export type NodeStatus = "idle" | "pending" | "ok" | "error";

export interface GraphNode {
  id: string;
  label: string;
  /** Nodes that draft or classify with the model, worth flagging as the costly ones. */
  usesModel?: boolean;
}

/** Rows top to bottom. The four workflow nodes share a row because they are alternatives. */
export const GRAPH_LAYERS: GraphNode[][] = [
  [{ id: "ingest", label: "ingest" }],
  [{ id: "classify_intent", label: "classify_intent", usesModel: true }],
  [{ id: "policy_gate", label: "policy_gate" }],
  [
    { id: "identity_verification", label: "identity", usesModel: true },
    { id: "knowledge_qa", label: "knowledge_qa", usesModel: true },
    { id: "claim_intake", label: "claim_intake", usesModel: true },
    { id: "general_assist", label: "general_assist", usesModel: true },
  ],
  [{ id: "compose_guidance", label: "compose_guidance" }],
  [{ id: "update_summary", label: "update_summary", usesModel: true }],
];

export const GRAPH_NODES: GraphNode[] = GRAPH_LAYERS.flat();

/** Conditional edges from builder.py, used to show what may run next mid-turn. */
const SUCCESSORS: Record<string, string[]> = {
  ingest: ["classify_intent", "update_summary"],
  classify_intent: ["policy_gate"],
  policy_gate: [
    "identity_verification",
    "knowledge_qa",
    "claim_intake",
    "general_assist",
  ],
  // The in-turn resume: once verified, control hands straight back to the interrupted
  // workflow rather than waiting for the customer to ask again.
  identity_verification: [
    "claim_intake",
    "knowledge_qa",
    "general_assist",
    "compose_guidance",
  ],
  knowledge_qa: ["compose_guidance"],
  claim_intake: ["compose_guidance"],
  general_assist: ["compose_guidance"],
  compose_guidance: ["update_summary"],
  update_summary: [],
};

export interface NodeState {
  status: NodeStatus;
  ms?: number;
  /** Order this node ran in, 1-based. Undefined if it did not run. */
  step?: number;
}

/**
 * Fold the streamed node events into a per-node view state.
 *
 * Events arrive on completion, so a node that is *running* has not reported yet. Rather
 * than guess which branch was taken, every possible successor of the last completed node
 * is marked `pending` while the turn is in flight - which is honest about what the graph
 * knows at that instant, and still shows the agent that work is happening.
 */
export function foldNodeStates(
  trace: NodeTrace[],
  thinking: boolean,
): Record<string, NodeState> {
  const states: Record<string, NodeState> = {};
  for (const node of GRAPH_NODES) states[node.id] = { status: "idle" };

  trace.forEach((entry, index) => {
    if (!states[entry.node]) return;
    states[entry.node] = {
      status: entry.status === "error" ? "error" : "ok",
      ms: entry.ms,
      step: index + 1,
    };
  });

  if (thinking) {
    const last = trace[trace.length - 1]?.node;
    const candidates = last ? (SUCCESSORS[last] ?? []) : ["ingest"];
    for (const id of candidates) {
      if (states[id]?.status === "idle") states[id] = { status: "pending" };
    }
  }

  return states;
}

/** Node names the backend sent that this view does not know how to draw. */
export function unknownNodes(trace: NodeTrace[]): string[] {
  const known = new Set(GRAPH_NODES.map((n) => n.id));
  return [...new Set(trace.map((t) => t.node).filter((n) => !known.has(n)))];
}
